import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Field, FieldType } from '../models/field.model';
import { FieldDependencyValidator } from '../utils/field-dependency.validator';

/**
 * Service for managing field dependencies in the form designer
 */
@Injectable({
  providedIn: 'root'
})
export class FormDependencyService {
  private currentFormFields = new BehaviorSubject<Field[]>([]);

  /**
   * Update the current form fields
   * @param fields - Array of all fields in the current form
   */
  updateFormFields(fields: Field[]): void {
    this.currentFormFields.next(fields);
  }

  /**
   * Get all Yes/No fields that can be used as dependencies
   * @param currentField - The field being edited (optional). If provided and is a sub-field,
   *                       only sibling fields within the same Repeatable field will be returned.
   * @returns Observable of options for dependency selection dropdown
   */
  getYesNoFields(currentField?: Field): Observable<{label: string, value: string}[]> {
    return this.currentFormFields.pipe(
      map(fields => {
        let searchFields: Field[] = fields;

        // If editing a sub-field of a repeatable field, only show sibling Yes/No fields
        if (currentField && (currentField as any).isSubField) {
          // Extract parent field ID from sub-field ID
          // Format: {parentId}_sub_{index}_{key}
          const parentFieldId = this.extractParentFieldId(currentField.id);

          if (parentFieldId) {
            // Find the parent repeatable field
            const parentField = fields.find(f => f.id === parentFieldId);

            if (parentField && parentField.type === FieldType.REPEATABLE_KEYWORD_FIELD) {
              // Use only the sub-fields from the parent repeatable field
              searchFields = (parentField as any).createdSubFields || [];
            }
          }
        }

        // Filter and map Yes/No fields
        return searchFields
          .filter(field =>
            // Original Yes/No fields
            field.type === FieldType.YES_NO ||
            // KeywordFields with YES_NO renderType
            (field.type === FieldType.KEYWORD_FIELD && field['renderType'] === FieldType.YES_NO)
          )
          .map(field => ({
            label: field.label,
            value: field.key
          }));
      })
    );
  }

  /**
   * Extract parent field ID from sub-field ID
   * @param subFieldId - Sub-field ID in format: {parentId}_sub_{index}_{key}
   * @returns Parent field ID or null
   */
  private extractParentFieldId(subFieldId: string): string | null {
    // Match pattern: anything before "_sub_"
    const match = subFieldId.match(/^(.+)_sub_\d+_/);
    return match ? match[1] : null;
  }

  /**
   * Get all available dependency fields (Yes/No type only)
   * @returns Observable of fields that can be used as dependencies
   */
  getAvailableDependencyFields(): Observable<Field[]> {
    return this.currentFormFields.pipe(
      map(fields => FieldDependencyValidator.getAvailableDependencyFields(fields))
    );
  }

  /**
   * Handle field deletion - clean up any dependency configurations
   * that reference the deleted field
   * @param deletedFieldKey - Key of the field that was deleted
   */
  handleFieldDeletion(deletedFieldKey: string): void {
    const currentFields = this.currentFormFields.value;
    const cleanedFields = FieldDependencyValidator.cleanupDependencies(deletedFieldKey, currentFields);
    this.updateFormFields(cleanedFields);
  }

  /**
   * Handle multiple field deletion - clean up any dependency configurations
   * that reference the deleted fields
   * @param deletedFieldKeys - Array of keys of fields that were deleted
   */
  handleMultipleFieldDeletion(deletedFieldKeys: string[]): void {
    let currentFields = this.currentFormFields.value;

    // Clean up dependencies for each deleted field
    deletedFieldKeys.forEach(deletedFieldKey => {
      currentFields = FieldDependencyValidator.cleanupDependencies(deletedFieldKey, currentFields);
    });

    this.updateFormFields(currentFields);
  }

  /**
   * Get the enabled state of a specific field
   * @param fieldKey - Key of the field to check
   * @param formValues - Current form values
   * @returns true if field should be enabled, false otherwise
   */
  getFieldEnabledState(fieldKey: string, formValues: {[key: string]: any}): boolean {
    const fields = this.currentFormFields.value;
    const field = fields.find(f => f.key === fieldKey);

    if (!field) return true;

    return FieldDependencyValidator.isFieldEnabled(field, formValues, fields);
  }

  getFieldVisibleState(fieldKey: string, formValues: {[key: string]: any}): boolean {
    const fields = this.currentFormFields.value;
    const field = fields.find(f => f.key === fieldKey);

    if (!field) return true;

    return FieldDependencyValidator.isFieldVisible(field, formValues, fields);
  }

  /**
   * Clear values of disabled fields
   * @param formValues - Current form values
   * @returns Updated form values with disabled field values cleared
   */
  clearDisabledFieldValues(formValues: {[key: string]: any}): {[key: string]: any} {
    const fields = this.currentFormFields.value;
    return FieldDependencyValidator.clearDisabledFieldValues(formValues, fields);
  }

  /**
   * Get fields that should participate in form validation
   * @param formValues - Current form values
   * @returns Array of fields that should be validated
   */
  getValidatableFields(formValues: {[key: string]: any}): Field[] {
    const fields = this.currentFormFields.value;
    return FieldDependencyValidator.getValidatableFields(fields, formValues);
  }

  /**
   * Check if setting a dependency would create a circular dependency
   * @param fieldKey - Key of the field that wants to set a dependency
   * @param dependsOnKey - Key of the field it wants to depend on
   * @returns true if this would create a circular dependency
   */
  wouldCreateCircularDependency(fieldKey: string, dependsOnKey: string): boolean {
    const fields = this.currentFormFields.value;
    return FieldDependencyValidator.hasCircularDependency(fieldKey, dependsOnKey, fields);
  }

  /**
   * Get the current form fields
   * @returns Current array of form fields
   */
  getCurrentFields(): Field[] {
    return this.currentFormFields.value;
  }

  /**
   * Get observable of current form fields
   * @returns Observable of current form fields
   */
  getCurrentFields$(): Observable<Field[]> {
    return this.currentFormFields.asObservable();
  }

  /**
   * Update a specific field's dependency configuration
   * @param fieldKey - Key of the field to update
   * @param dependsOn - Key of the field to depend on (null to remove dependency)
   * @param enableWhen - Value that enables the field (true for Yes, false for No)
   */
  updateFieldDependency(fieldKey: string, dependsOn: string | null, enableWhen?: boolean): void {
    const fields = this.currentFormFields.value;
    const updatedFields = fields.map(field => {
      if (field.key === fieldKey) {
        const updatedField = { ...field };
        if (dependsOn) {
          updatedField.dependsOn = dependsOn;
          updatedField.enableWhen = enableWhen ?? true;
        } else {
          delete updatedField.dependsOn;
          delete updatedField.enableWhen;
          delete updatedField.disabledBehavior;
        }
        return updatedField;
      }
      return field;
    });

    this.updateFormFields(updatedFields);
  }

  /**
   * Get fields that depend on a specific field
   * @param fieldKey - Key of the field to check
   * @returns Array of fields that depend on the specified field
   */
  getFieldDependents(fieldKey: string): Field[] {
    const fields = this.currentFormFields.value;
    return fields.filter(field => field.dependsOn === fieldKey);
  }
}
